<script>
    import TaskModel from 'models/TaskModel'
    import {onDestroy} from 'svelte';
    import {SvelteMap} from 'svelte/reactivity';
    import TextSimilarityUtil from 'util/TextSimilarityUtil';
    import Util from 'util/util'
    import Icon from 'views/components/icon/Icon.svelte'
    import QuickInput from 'views/components/quickInput/QuickInput.svelte'
    import QuickSelect from 'views/components/quickSelect/QuickSelect.svelte'
    import GapNumberInput from 'views/components/taskGroups/tasks/template41/GapNumberInput.svelte'
    import {GAP_TYPE, getFragmentsFromTaskInfo, hashFragment, isNumberAnswerCorrect} from 'views/components/taskGroups/tasks/template41/GapTextUtil'
    import Tooltip from 'views/components/toolTip/Tooltip.svelte'

    const {
        model,
        response,
        syncResponse,
        taskContainerElement,
        isAnswerSummary = $bindable(false),
    } = $props()

    // svelte-ignore state_referenced_locally
    const fragments = $state(getFragmentsFromTaskInfo(model))
    // svelte-ignore state_referenced_locally
    const dragAnswerOptions = $state(model.get('task_info_json').dragChoices ?? [])
    // svelte-ignore state_referenced_locally
    const gradingMode = $state(model.get('grading_mode'))

    // svelte-ignore state_referenced_locally
    const givenResponse = $state(
        Array.isArray(response?.get('json_answer')) ?
            new SvelteMap(response?.get('json_answer').map((gapAnswer) => [ gapAnswer.id, gapAnswer.answer ])) : new SvelteMap()
    )

    // For draggable gaps, apply given response to relevant gap, if any.
    for (const [id, answer] of givenResponse.entries()) {
        const gapFragment = fragments.find((fragment) => fragment instanceof Object && fragment.id === id)
        if (gapFragment?.type === GAP_TYPE.DRAG) {
            const dragAnswerOption = dragAnswerOptions.find(({text}) => text === answer.text)
            if (dragAnswerOption) {
                dragAnswerOption.placedAt = id
            }
        }
    }

    let isAnswerLocked = $state(isAnswerSummary)
    let isShowingAnswer = $state(false)
    let isDraggingAnswerOption = $state(false)
    // If true, show model answer in place of given response, useful for author preview and
    // student answers list model answer.
    // svelte-ignore state_referenced_locally
    let isShowingAnswerAsResponse = $state(isAnswerSummary && response === undefined)

    function saveResponse() {
        // Answer summary is read only, no save needed
        if (isAnswerSummary) {
            return
        }
        // If current response and initial response are empty, don't sent response to backend
        // to prevent sending empty array to backend.
        if (givenResponse.size === 0 && _.size(response?.get('json_answer')) === 0) {
            return
        }
        // Save given response in a format that the backend understands.
        const newResponse = []
        for (const [id, answer] of givenResponse.entries()) {
            newResponse.push({ id, answer })
        }
        syncResponse(newResponse)
    }
    const saveResponseDebounced = _.debounce(saveResponse, 1000)

    function getGapInputValue(gapFragment) {
        return givenResponse.get(gapFragment.id)?.text ?? ''
    }

    function setGapInputValue(gapFragment, value) {
        let newAnswerValue = value

        if (gapFragment.type === GAP_TYPE.OPEN) {
            newAnswerValue = Util.normalizeCharacters(value)
        }

        if (newAnswerValue) {
            // If the gap of is of type choice, remove the answer if when the default empty option
            // of the dropdown is selected or if choice was already active.
            if (
                gapFragment.type === GAP_TYPE.CHOICE &&
                (
                    newAnswerValue === -1 ||
                    newAnswerValue === givenResponse.get(gapFragment.id)?.text
                )
            ) {
                givenResponse.delete(gapFragment.id)
            } else {
                givenResponse.set(gapFragment.id, {text: newAnswerValue})
            }
        } else {
            givenResponse.delete(gapFragment.id)
        }

        saveResponseDebounced()
    }

    function gradeGapTypeOpen(gapFragment) {
        if (isShowingAnswerAsResponse) {
            return {
                gapResponseText: gapFragment.answers.find((answer) => answer.correct)?.text || '…',
            }
        }
        const gapResponse = givenResponse.get(gapFragment.id)
        let gapResponseText = gapResponse?.text
        let isCorrect = false
        let isIncorrect = false
        if (gradingMode === 'auto') {
            if (gapResponse && gapResponseText !== undefined) {
                isCorrect = gapFragment.answers.some((modelAnswerOption) => {
                    if (gapFragment.gradingStrictness === TaskModel.TEXT_SIMILARITY_GRADING) {
                        return TextSimilarityUtil.getTextSimilarity(
                            gapResponseText,
                            modelAnswerOption.text
                        ) >= TextSimilarityUtil.SIMILARITY_THRESHOLD
                    }
                    if (gapFragment.gradingStrictness === TaskModel.CASE_INSENSITIVE_GRADING) {
                        return gapResponseText.toLowerCase() === modelAnswerOption.text.toLowerCase()
                    }
                    return gapResponseText === modelAnswerOption.text
                })
            }
            isIncorrect = !isCorrect
        }
        if (!gapResponseText) {
            gapResponseText = '…'
        }
        // If no response is given for this gap or response is empty AND the gap is allowed to be empty,
        // mark this gap as correct.
        if ((!gapResponse || !gapResponseText) && gapFragment.answers[0].text === '') {
            isCorrect = true
            isIncorrect = false
        }
        return {
            isCorrect: gradingMode === 'student' ? false : isCorrect,
            isIncorrect,
            gapResponseText,
            tooltipText: isCorrect ? null : gapFragment.answers[0].text,
        }
    }

    function gradeGapTypeNumber(gapFragment) {
        if (isShowingAnswerAsResponse) {
            return {
                gapResponseText: gapFragment.answers[0].text,
            }
        }

        const gapResponseText = givenResponse.get(gapFragment.id)?.text
        let isCorrect = false
        let isIncorrect = false

        if (gradingMode === 'auto') {
            if (gapResponseText !== undefined) {
                isCorrect = isNumberAnswerCorrect(gapResponseText, gapFragment.answers[0])
            }
            isIncorrect = !isCorrect
        }

        return {
            isCorrect,
            isIncorrect,
            gapResponseText: gapResponseText ?? '…',
            tooltipText: isCorrect ? null : gapFragment.answers[0].text
        }
    }

    function gradeGapTypeChoiceOrDrag(gapFragment) {
        const correctAnswer = gapFragment.answers.find(({correct}) => correct)
        if (isShowingAnswerAsResponse) {
            return {
                gapResponseText: correctAnswer.text,
            }
        }
        let gapResponseText = givenResponse.get(gapFragment.id)?.text
        const isCorrect = correctAnswer.text === gapResponseText
        if (!gapResponseText && isAnswerSummary) {
            gapResponseText = '…'
        }
        return {
            isCorrect,
            isIncorrect: !isCorrect,
            // Show a no-break space character as a placeholder for an empty answer.
            gapResponseText: gapResponseText ?? '\u00A0',
            tooltipText: isCorrect ? null : correctAnswer.text,
        }
    }

    function gradeGapTypeChoiceAnswerOption(gapFragment, gapAnswerOption) {
        const gapResponseText = givenResponse.get(gapFragment.id)?.text
        const isIncorrect = isShowingAnswer &&
            gapAnswerOption.correct === false &&
            gapResponseText !== undefined &&
            gapAnswerOption.text === gapResponseText
        return {
            isCorrect:
                isShowingAnswer &&
                    gapAnswerOption.correct === true &&
                    gapAnswerOption.text === gapResponseText,
            isIncorrect,
            isMissing:
                isShowingAnswer &&
                    gapAnswerOption.correct === true &&
                    gapAnswerOption.text !== gapResponseText,
        }
    }

    function getGapTypeChoiceWithDropdownOptions(gapFragment) {
        return [
            {
                label: '\u00A0',
                value: -1,
            }
        ].concat(gapFragment.answers.map((answer) => {
            return {
                value: answer.text,
                label: Util.stripTags(answer.text),
            }
        }))
    }

    function initGapDroppable(el, gapFragment) {
        jQuery(el).droppable({
            tolerance: 'pointer',
            scope: `task-${model.id}`,
            drop: (e, ui) => {
                const dragElementIndex = parseInt(ui.draggable.data('gap-answer-option-index'))
                const previousGapId = dragAnswerOptions[dragElementIndex].placedAt

                // Do nothing if answer option is dragged to the same gap it was at already.
                if (previousGapId === gapFragment.id) {
                    return
                }
                // Remove answer options placed in this gap.
                dragAnswerOptions.forEach((answerOption, index) => {
                    if (index !== dragElementIndex && answerOption.placedAt === gapFragment.id) {
                        delete answerOption.placedAt
                    }
                })
                dragAnswerOptions[dragElementIndex].placedAt = gapFragment.id
                setGapInputValue(gapFragment, dragAnswerOptions[dragElementIndex].text)
                // If answer option was dragged from a different answer option, remove the
                // answer for this gap to prevent the answer option from duplicating.
                if (previousGapId !== undefined) {
                    givenResponse.delete(previousGapId)
                    saveResponseDebounced()
                }
            },
        })
    }

    function initDragAnswerOptionsDroppable(el) {
        jQuery(el).droppable({
            tolerance: 'pointer',
            scope: `task-${model.id}`,
            drop: (e, ui) => {
                const dragElementIndex = parseInt(ui.draggable.data('gap-answer-option-index'))
                givenResponse.delete(dragAnswerOptions[dragElementIndex].placedAt)
                saveResponseDebounced()
                delete dragAnswerOptions[dragElementIndex].placedAt
            },
        })
    }

    function initDraggable(el) {
        jQuery(el).draggable({
            revert: true,
            revertDuration: 100,
            cursorAt: { top: 18, left: 20 },
            cursor: 'move',
            containment: taskContainerElement,
            scope: `task-${model.id}`,
            scrollSensitivity: 60,
            scrollSpeed: 40,
            disabled: isAnswerLocked,
            zIndex: 1,
            start: () => {
                isDraggingAnswerOption = true
            },
            stop: () => {
                isDraggingAnswerOption = false
            },
        })
    }

    export function showAnswer() {
        if (APPLICATION === 'author') {
            return
        }

        // If a student clicks the show answer button while the debounce is active,
        // save the response first and then show the answer
        saveResponseDebounced.cancel()
        saveResponse()

        isAnswerLocked = true
        isShowingAnswer = true
    }

    export function showAuthorAnswer() {
        isAnswerLocked = true
        isShowingAnswerAsResponse = true
    }

    export function hideAnswer() {
        isAnswerLocked = false
        isShowingAnswer = false
    }

    export function lockAnswer() {
        isAnswerLocked = true
    }
    export function unlockAnswer() {
        isAnswerLocked = false
    }

    onDestroy(() => {
        saveResponseDebounced.cancel()
        saveResponse()
    })
</script>

{#snippet modelAnswerGap(grading)}
    <span
        class="gap-input gap-input__answer"
        class:gap-input__answer--correct={grading.isCorrect}
        class:gap-input__answer--incorrect={grading.isIncorrect}
        class:gap-input__answer--missing={grading.isMissing}
        class:gap-input__answer--compact={isAnswerSummary}
        {@attach (gapElement) => {
            // Make the gap at least as wide as the tooltip to prevent
            // the tooltip from falling outside of the viewport.
            if (isShowingAnswer && grading.tooltipText) {
                const tooltipElement = gapElement.querySelector('.tooltip')
                if (tooltipElement?.clientWidth > 100) {
                    gapElement.style.minWidth = `${tooltipElement?.clientWidth}px`
                }
            }
        }}
    >
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        <span>{@html grading.gapResponseText}</span>
        {#if grading.tooltipText && isShowingAnswer}
            <Tooltip
                isOpen={true}
                canBeClosed={false}
                alignment="top-left"
                isCompact={true}
            >
                <Icon name="checkmark"></Icon>
                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                {@html grading.tooltipText}
            </Tooltip>
        {/if}
    </span>
{/snippet}

{#snippet draggableItem(gapAnswerOptionText, answerIndex)}
    <span
        class="draggable-item"
        data-gap-answer-option-index={answerIndex}
        {@attach initDraggable}
    >
        <Icon name="move"></Icon>
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        <span>{@html gapAnswerOptionText}</span>
    </span>
{/snippet}

<section class="fragments" class:fragments--compact={isAnswerSummary}>
    {#each fragments as fragment, fragmentIndex (hashFragment(fragment, fragmentIndex))}
        {const isGap = fragment instanceof Object}
        {#if isGap}
            <span class="gap-container">
                {#if fragment.type === GAP_TYPE.OPEN}
                    {#if isAnswerLocked}
                        {@render modelAnswerGap(gradeGapTypeOpen(fragment))}
                    {:else}
                        <span
                            class="gap-input gap-input__type-open"
                            class:gap-input--is-dragging={isDraggingAnswerOption}
                        >
                            <QuickInput
                                bind:value={() => getGapInputValue(fragment), (v) => setGapInputValue(fragment, v)}
                                noAutoCapitalize={true}
                                noAutoCorrect={true}
                                displayInline={true}
                                isWide={fragment.isBig}
                            ></QuickInput>
                        </span>
                    {/if}
                {:else if fragment.type === GAP_TYPE.CHOICE}
                    {#if isAnswerSummary}
                        {@render modelAnswerGap(gradeGapTypeChoiceOrDrag(fragment))}
                    {:else}
                        <span
                            class="gap-input gap-input__type-choice"
                            class:gap-input__type-choice-select={fragment.useDropdownInput}
                            class:gap-input--is-dragging={isDraggingAnswerOption}
                        >
                            {#if fragment.useDropdownInput}
                                {#if isAnswerLocked}
                                    {@render modelAnswerGap(gradeGapTypeChoiceOrDrag(fragment))}
                                {:else}
                                    <QuickSelect
                                        inline={true}
                                        items={getGapTypeChoiceWithDropdownOptions(fragment)}
                                        defaultValue={getGapInputValue(fragment)}
                                        callback={({value}) => setGapInputValue(fragment, value)}
                                    ></QuickSelect>
                                {/if}
                            {:else}
                                {#each fragment.answers as answer (answer.text)}
                                    {const grading = $derived(gradeGapTypeChoiceAnswerOption(fragment, answer))}
                                    {const isSelected = $derived(isShowingAnswerAsResponse ?
                                        answer.correct : givenResponse.get(fragment.id)?.text === answer.text
                                    )}
                                    <button
                                        class="gap-input__type-choice-button"
                                        class:gap-input__type-choice-button--selected={isSelected}
                                        class:gap-input__answer--correct={grading.isCorrect}
                                        class:gap-input__answer--incorrect={grading.isIncorrect}
                                        class:gap-input__answer--missing={grading.isMissing}
                                        onclick={() => setGapInputValue(fragment, answer.text)}
                                        disabled={isAnswerLocked}
                                    >
                                        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                        {@html answer.text}
                                    </button>
                                {/each}
                            {/if}
                        </span>
                    {/if}
                {:else if fragment.type === GAP_TYPE.DRAG}
                    {#if isAnswerLocked}
                        {@render modelAnswerGap(gradeGapTypeChoiceOrDrag(fragment))}
                    {:else}
                        <span
                            class="gap-input gap-input__type-drag gap-input__type-drag__target"
                            {@attach (el) => initGapDroppable(el, fragment)}
                        >
                            {const gapResponseText = $derived(getGapInputValue(fragment))}
                            {#if gapResponseText}
                                {const dragAnswerOptionIndex = $derived(dragAnswerOptions.findIndex(({placedAt}) => {
                                    return placedAt === fragment.id
                                }))}
                                {@render draggableItem(
                                    gapResponseText,
                                    dragAnswerOptionIndex,
                                    fragment
                                )}
                            {/if}
                        </span>
                    {/if}
                {:else if fragment.type === GAP_TYPE.NUMBER}
                    {#if isAnswerLocked}
                        {@render modelAnswerGap(gradeGapTypeNumber(fragment))}
                    {:else}
                        <span
                            class="gap-input gap-input__type-number"
                            class:gap-input--is-dragging={isDraggingAnswerOption}
                        >
                            <GapNumberInput
                                bind:value={() => getGapInputValue(fragment),
                                (v) => setGapInputValue(fragment, v)}
                            ></GapNumberInput>
                        </span>
                    {/if}
                {/if}
            </span>
        {:else if fragment === '\n'}
            {#if fragments[fragmentIndex + 1] === '\n'}
                <hr />
            {:else if fragments[fragmentIndex - 1] !== '\n'}
                <br />
            {/if}
        {:else}
            <!-- Insert space only between plain text fragments -->
            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
            {@html `${fragment}${_.isString(fragments[fragmentIndex + 1]) ? ' ' : ''}`}
        {/if}
    {/each}
</section>

{#if dragAnswerOptions.length && !isAnswerSummary}
    <section
        class="fragments drag-answer-options"
        class:drag-answer-options--answer={isShowingAnswer}
        {@attach initDragAnswerOptionsDroppable}
    >
        {#each dragAnswerOptions as dragAnswerOption, index (index)}
            {#if dragAnswerOption.placedAt === undefined}
                {@render draggableItem(dragAnswerOption.text, index)}
            {/if}
        {/each}
    </section>
{/if}

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.fragments {
  font-size: 16px;
  line-height: 2.4;
}
.fragments--compact {
  line-height: 2.2;
}
.fragments hr {
  border: unset;
  margin: 0.5em;
}

.gap-container {
  margin: 0 4px;
  vertical-align: middle;
  line-height: normal;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}

.gap-input {
  position: relative;
  min-width: 100px;
}
.gap-input--is-dragging {
  opacity: 0.5;
}
.gap-input__type-open :global(input),
.gap-input__type-open :global(input + *), .gap-input__type-number :global(input),
.gap-input__type-number :global(input + *) {
  padding: 8px 12px;
}
.gap-input__type-choice {
  display: inline-flex;
  gap: 4px;
  min-width: unset;
}
.gap-input__type-choice.gap-input__type-choice-select :global([component="quick-select-select-container"]) {
  padding: 8px 12px;
  gap: 4px;
  font-size: 16px;
  border-radius: 12px;
}
.gap-input__type-choice .gap-input__type-choice-button {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.gap-input__type-choice .gap-input__type-choice-button :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.gap-input__type-choice .gap-input__type-choice-button {
  background-color: #fff;
  color: #122d40;
  border: solid 1px hsl(40, 4%, 84%);
}
.gap-input__type-choice .gap-input__type-choice-button :global(svg) {
  fill: #122d40;
}
.gap-input__type-choice .gap-input__type-choice-button > :global(*) {
  display: inline-flex;
}
.gap-input__type-choice .gap-input__type-choice-button > :global(*:empty) {
  display: none;
}
.gap-input__type-choice .gap-input__type-choice-button {
  font-size: 16px;
  margin: 2px 0;
}
.gap-input__type-choice .gap-input__type-choice-button:not(.gap-input__type-choice .gap-input__type-choice-button--selected):not(:disabled):hover {
  background-color: hsl(40, 20%, 96%);
}
.gap-input__type-choice .gap-input__type-choice-button:disabled {
  cursor: not-allowed;
}
.gap-input__type-choice .gap-input__type-choice-button--selected {
  color: hsl(205, 60%, 44%);
  background-color: rgba(17.6%, 48.4%, 70.4%, 0.1);
}
.gap-input__type-drag__target {
  margin: 3px 0;
  height: 36px;
  border: 1px dashed #9a9bb5;
  border-radius: 16px;
  transition: background-color 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.gap-input__type-drag__target:has(.draggable-item) {
  min-width: unset;
  border: unset;
}
.gap-input__type-drag__target:global(.ui-droppable-active), .gap-input__type-drag__target:global(.ui-droppable-active) .draggable-item:not(:global(.ui-draggable-dragging)) {
  color: hsl(205, 60%, 44%);
  border-color: hsl(205, 60%, 44%);
  background-color: rgba(17.6%, 48.4%, 70.4%, 0.1);
}
.gap-input__type-drag__target:global(.ui-droppable-hover), .gap-input__type-drag__target:global(.ui-droppable-hover) .draggable-item:not(:global(.ui-draggable-dragging)) {
  color: hsl(205, 60%, 44%);
  background-color: rgba(17.6%, 48.4%, 70.4%, 0.5);
}
.gap-input__answer {
  margin: 2px 0;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid hsl(40, 4%, 84%);
  border-radius: 16px;
}
.gap-input__answer--correct {
  color: #21894d !important;
  background-color: rgba(63, 209, 125, 0.1) !important;
  border-color: #21894d !important;
}
.gap-input__answer--incorrect {
  color: #fb455b !important;
  background-color: rgba(252, 119, 135, 0.1) !important;
  border-color: #fb455b !important;
}
.gap-input__answer--missing {
  color: #122d40 !important;
  background-color: rgba(63, 209, 125, 0.1) !important;
  border-color: #122d40 !important;
  border-style: dashed !important;
}
.gap-input__answer--compact {
  min-width: unset;
  padding: 4px 8px !important;
  margin: 0 !important;
  border-radius: 12px;
}

.drag-answer-options {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.drag-answer-options--answer .draggable-item {
  opacity: 0.8;
}

.draggable-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid hsl(40, 4%, 84%);
  border-radius: 16px;
  line-height: normal;
  user-select: none;
}
.draggable-item:not(:global(.ui-draggable-disabled)):hover {
  background-color: hsl(40, 20%, 96%);
}
.draggable-item:global(.ui-draggable-dragging) {
  opacity: 0.8;
  color: #122d40;
}
.draggable-item:global(.ui-draggable-handle) {
  cursor: move;
}
.draggable-item:global(.ui-draggable-disabled) {
  cursor: not-allowed;
}
.draggable-item :global(svg) {
  width: 16px;
  height: 16px;
}</style>
